import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  FileText,
  Award,
  UserCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminApplications() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [creatingCert, setCreatingCert] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    let filtered = applications;
    
    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.institution_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.country?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }
    
    setFilteredApps(filtered);
  }, [searchTerm, statusFilter, applications]);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/applications`, getAuthHeader());
      setApplications(response.data);
      setFilteredApps(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleViewApplication = (app) => {
    setSelectedApp(app);
    setAdminNotes(app.admin_notes || '');
    setDialogOpen(true);
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedApp) return;
    
    setUpdating(true);
    try {
      await axios.put(
        `${API}/applications/${selectedApp.id}`,
        { status, admin_notes: adminNotes },
        getAuthHeader()
      );
      toast.success(`Application ${status}`);
      setDialogOpen(false);
      fetchApplications();
    } catch (error) {
      console.error('Error updating application:', error);
      toast.error('Failed to update application');
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateCertificate = async (app) => {
    setCreatingCert(true);
    try {
      const response = await axios.post(
        `${API}/admin/certificates/create`,
        { 
          application_id: app.id,
          member_name: app.institution_name,
          email: app.email,
          membership_type: app.membership_type,
          country: app.country
        },
        getAuthHeader()
      );
      toast.success(`Certificate created: ${response.data.certificate_number}`);
      
      // Update application status to active
      await axios.put(
        `${API}/applications/${app.id}`,
        { status: 'active', admin_notes: adminNotes || 'Certificate issued' },
        getAuthHeader()
      );
      
      setDialogOpen(false);
      fetchApplications();
    } catch (error) {
      console.error('Error creating certificate:', error);
      toast.error(error.response?.data?.detail || 'Failed to create certificate');
    } finally {
      setCreatingCert(false);
    }
  };

  const handleApproveAndCreateCertificate = async () => {
    if (!selectedApp) return;
    
    setUpdating(true);
    try {
      // First approve
      await axios.put(
        `${API}/applications/${selectedApp.id}`,
        { status: 'approved', admin_notes: adminNotes },
        getAuthHeader()
      );
      
      // Then create certificate
      const response = await axios.post(
        `${API}/admin/certificates/create`,
        { 
          application_id: selectedApp.id,
          member_name: selectedApp.institution_name,
          email: selectedApp.email,
          membership_type: selectedApp.membership_type,
          country: selectedApp.country
        },
        getAuthHeader()
      );
      
      // Update to active
      await axios.put(
        `${API}/applications/${selectedApp.id}`,
        { status: 'active', admin_notes: adminNotes || 'Certificate issued' },
        getAuthHeader()
      );
      
      toast.success(`Approved & Certificate created: ${response.data.certificate_number}`);
      setDialogOpen(false);
      fetchApplications();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve and create certificate');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="badge-pending px-2 py-1 rounded text-xs font-medium">Pending</span>;
      case 'approved':
        return <span className="badge-approved px-2 py-1 rounded text-xs font-medium">Approved</span>;
      case 'active':
        return <span className="badge-active px-2 py-1 rounded text-xs font-medium">Active</span>;
      case 'rejected':
        return <span className="badge-rejected px-2 py-1 rounded text-xs font-medium">Rejected</span>;
      default:
        return <span className="badge-inactive px-2 py-1 rounded text-xs font-medium">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0E285B] mb-8">
        Membership Applications
      </h1>

      {/* Filters */}
      <div className="bg-white p-4 rounded-sm border border-slate-200 shadow-sm mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, or country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-sm border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full admin-table">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left">Institution</th>
                <th className="px-6 py-3 text-left">Country</th>
                <th className="px-6 py-3 text-left">Type</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Date</th>
                <th className="px-6 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApps.map((app, index) => (
                <tr key={app.id} data-testid={`application-row-${index}`}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-[#0E285B]">{app.institution_name}</p>
                      <p className="text-sm text-slate-500">{app.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{app.country}</td>
                  <td className="px-6 py-4 text-slate-600 capitalize">{app.membership_type}</td>
                  <td className="px-6 py-4">{getStatusBadge(app.status)}</td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {new Date(app.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewApplication(app)}
                      data-testid={`view-app-${index}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredApps.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No applications found</p>
          </div>
        )}
      </div>

      {/* Application Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display']">Application Details</DialogTitle>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#F5A623] font-medium">Institution Name</p>
                  <p className="font-medium text-[#0E285B]">{selectedApp.institution_name}</p>
                </div>
                <div>
                  <p className="text-sm text-[#F5A623] font-medium">Email</p>
                  <a href={`mailto:${selectedApp.email}`} className="font-medium text-[#0E285B] hover:underline">{selectedApp.email}</a>
                </div>
                <div>
                  <p className="text-sm text-[#F5A623] font-medium">Country</p>
                  <p className="font-medium text-[#0E285B]">{selectedApp.country}</p>
                </div>
                <div>
                  <p className="text-sm text-[#F5A623] font-medium">Membership Type</p>
                  <p className="font-medium text-[#0E285B] capitalize">{selectedApp.membership_type}</p>
                </div>
                <div>
                  <p className="text-sm text-[#F5A623] font-medium">Position</p>
                  <p className="font-medium text-[#0E285B]">{selectedApp.position_title || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-[#F5A623] font-medium">Website</p>
                  {selectedApp.website ? (
                    <a href={selectedApp.website} target="_blank" rel="noopener noreferrer" className="font-medium text-[#0E285B] hover:underline">{selectedApp.website}</a>
                  ) : (
                    <p className="font-medium text-slate-400">N/A</p>
                  )}
                </div>
              </div>

              <div>
                <p className="text-sm text-[#F5A623] font-medium mb-2">Motivation Message</p>
                <p className="text-slate-700 bg-slate-50 p-4 rounded-sm border border-slate-200">
                  {selectedApp.motivation_message}
                </p>
              </div>

              <div>
                <p className="text-sm text-[#F5A623] font-medium mb-2">Documents</p>
                {selectedApp.documents?.length > 0 ? (
                  <ul className="space-y-2">
                    {selectedApp.documents.map((doc, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                        <FileText className="h-4 w-4 text-[#F5A623]" />
                        {doc.filename}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-slate-400">No documents uploaded</p>
                )}
              </div>

              <div>
                <p className="text-sm text-[#F5A623] font-medium mb-2">Admin Notes</p>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this application..."
                  className="min-h-[100px] border-slate-200 text-slate-700"
                  data-testid="admin-notes"
                />
              </div>

              <div>
                <p className="text-sm text-[#F5A623] font-medium mb-2">Current Status</p>
                {getStatusBadge(selectedApp.status)}
              </div>

              {/* Action Buttons based on status */}
              {selectedApp.status === 'pending' && (
                <div className="space-y-3 pt-4 border-t border-slate-200">
                  <Button
                    onClick={handleApproveAndCreateCertificate}
                    disabled={updating}
                    className="w-full bg-[#0E285B] hover:bg-[#0E285B]/90 text-white h-12"
                    data-testid="approve-create-cert-btn"
                  >
                    {updating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Award className="h-4 w-4 mr-2" />}
                    Approve & Create Certificate
                  </Button>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleUpdateStatus('approved')}
                      disabled={updating}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid="approve-btn"
                    >
                      {updating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Approve Only
                    </Button>
                    <Button
                      onClick={() => handleUpdateStatus('rejected')}
                      disabled={updating}
                      variant="outline"
                      className="flex-1 border-red-500 text-red-600 hover:bg-red-50"
                      data-testid="reject-btn"
                    >
                      {updating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                      Reject
                    </Button>
                  </div>
                </div>
              )}

              {selectedApp.status === 'approved' && (
                <div className="space-y-3 pt-4 border-t border-slate-200">
                  <Button
                    onClick={() => handleCreateCertificate(selectedApp)}
                    disabled={creatingCert}
                    className="w-full bg-[#F5A623] hover:bg-[#F5A623]/90 text-[#0E285B] h-12 font-semibold"
                    data-testid="create-cert-btn"
                  >
                    {creatingCert ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Award className="h-4 w-4 mr-2" />}
                    Create Certificate
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    Application approved. Create certificate to activate membership.
                  </p>
                </div>
              )}

              {selectedApp.status === 'active' && (
                <div className="pt-4 border-t border-slate-200">
                  <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-center">
                    <UserCheck className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="text-green-800 font-medium">Membership Active</p>
                    <p className="text-sm text-green-600">Certificate has been issued</p>
                  </div>
                </div>
              )}

              {selectedApp.status === 'rejected' && (
                <div className="pt-4 border-t border-slate-200">
                  <Button
                    onClick={() => handleUpdateStatus('pending')}
                    disabled={updating}
                    variant="outline"
                    className="w-full border-slate-300"
                    data-testid="reopen-btn"
                  >
                    {updating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Clock className="h-4 w-4 mr-2" />}
                    Reopen Application
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
